import { t } from "i18next";
import type { ReleaseEnvironment } from "@packages/commons";
import type { ImportReleaseFormValues } from "./types";

/** Returns `Release {n}`, defaulting to 1 for missing data. */
export const getReleaseUiName = (release: { releaseNo?: number }) =>
  `Release ${release.releaseNo ?? 1}`;

/**
 * Returns the display label for a release environment, translated via i18next.
 */
export const getReleaseEnvLabel = (env?: ReleaseEnvironment): string => {
  if (!env) return t("release_centre.unknown_environment");
  if (env === "test") return t("release_centre.development_environment");
  return t("release_centre.production_environment");
};

/** Builds the `releases/import` request body from the import form. The file must already be selected. */
export const buildImportReleasePayload = (projectId: string, data: ImportReleaseFormValues) => {
  const payload: Record<string, any> = {
    model: projectId,
    payload: data.file.data.compressed,
    active: data.active ?? true,
    preserveConnections: data.preserveConnections ?? true,
    preserveVariables: data.preserveVariables ?? true,
    preserveDerivedTables: data.preserveDerivedTables ?? false,
    preserveMappings: data.preserveMappings ?? false,
  };

  if (data.description?.trim()) {
    payload.description = data.description.trim();
  }

  if (data.releaseNo?.trim()) {
    const num = Number.parseInt(data.releaseNo.trim(), 10);
    if (!Number.isNaN(num) && num > 0) {
      payload.releaseNo = num;
    }
  }

  return payload;
};

/**
 * A release as the JSON its raw download saves: with one graph, its rule graph as JSON (what older tooling reads). The
 * compiled `decisionGraph` the release was fetched with is left out, as `rule_graph` is read back from it.
 */
export const releaseDownloadJson = (release: object | null | undefined): string => {
  if (!release) return JSON.stringify(release ?? null, null, 2);
  const { decisionGraph: _decisionGraph, ...rest } = release as Record<string, unknown>;
  return JSON.stringify(rest, null, 2);
};
